// ACTA_GMAO_INSTALLED_CITY_MAP_V3
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { MagnifyingGlass } from "@phosphor-icons/react";
import {
  CircleMarker,
  MapContainer,
  Popup,
  Tooltip,
} from "react-leaflet";
import { Link, Navigate } from "react-router-dom";

import * as gmaoMapApi from "@/api/gmaoMap";
import ActaLocalBasemap from "@/components/maps/ActaLocalBasemap";
import { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { hasPageAccess } from "@/lib/accessPermissions";
import { normalizeMapSearch } from "@/lib/maps/mapSearch";
import { GMAO_STATUS_COLORS } from "@/lib/themeColors";

const STATUS_COLORS = GMAO_STATUS_COLORS;

const safeNumber = (value) =>
  Number.isFinite(Number(value)) ? Number(value) : 0;

function centerOf(rows) {
  if (!rows.length) return [46.603354, 1.888334];

  return [
    rows.reduce(
      (sum, row) => sum + Number(row.latitude),
      0
    ) / rows.length,
    rows.reduce(
      (sum, row) => sum + Number(row.longitude),
      0
    ) / rows.length,
  ];
}

function statusRank(status) {
  return {
    broken_down: 0,
    attention_required: 1,
    operational: 2,
  }[status] ?? 9;
}

function statusForMachines(machines) {
  return [...machines]
    .sort(
      (left, right) =>
        statusRank(left.operationalStatus) -
        statusRank(right.operationalStatus)
    )[0]?.operationalStatus || "operational";
}

function machineMatches(machine, query) {
  const searchable = normalizeMapSearch(
    [
      machine.name,
      machine.serialNumber,
      machine.clientFirstName,
      machine.clientLastName,
      machine.companyName,
      machine.clientName,
    ]
      .filter(Boolean)
      .join(" ")
  );

  return searchable.includes(query);
}

export default function GmaoMap() {
  const { user } = useAuth();
  const canView = hasPageAccess(user, "gmao", "machines");

  const [fleet, setFleet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      setFleet(await gmaoMapApi.getInstalledCityMap());
    } catch (requestError) {
      setError(
        formatApiError(requestError.response?.data?.detail) ||
          "Impossible de charger la carte."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (canView) load();
  }, [canView, load]);

  const clusters = useMemo(
    () =>
      Array.isArray(fleet?.clusters)
        ? fleet.clusters
        : [],
    [fleet]
  );

  const rows = useMemo(() => {
    const query = normalizeMapSearch(search);

    if (!query) return clusters;

    return clusters
      .map((cluster) => {
        const cityMatches = normalizeMapSearch(
          cluster.city
        ).includes(query);

        const machines = cityMatches
          ? cluster.machines || []
          : (cluster.machines || []).filter((machine) =>
              machineMatches(machine, query)
            );

        if (!machines.length) return null;

        return {
          ...cluster,
          machines,
          machineCount: machines.length,
          operationalStatus: statusForMachines(machines),
        };
      })
      .filter(Boolean);
  }, [clusters, search]);

  if (!canView) {
    return <Navigate to="/app" replace />;
  }

  return (
    <main
      className="acta-gmao-page-standard w-full max-w-full min-w-0 p-4 sm:p-5 lg:p-8"
      data-testid="gmao-map-page"
    >
      <section className="lirah-card overflow-hidden">
        <div className="border-b border-[var(--do-border)] p-4">
          <label className="relative block">
            <MagnifyingGlass
              size={18}
              weight="bold"
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[var(--do-muted)]"
            />
            <input
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Rechercher une machine, une ville ou un client..."
              className="h-11 w-full rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] pl-10 pr-4 text-sm outline-none transition focus:border-[var(--do-blue)]"
              data-testid="gmao-map-search"
            />
          </label>
        </div>

        {loading ? (
          <div className="flex min-h-[560px] items-center justify-center text-sm text-[var(--do-muted)]">
            Chargement de la carte…
          </div>
        ) : error ? (
          <div className="m-4 rounded-xl border border-[var(--do-border)] bg-[var(--do-accent-soft)] p-4 text-sm text-[var(--do-accent)]">
            {error}
          </div>
        ) : !rows.length ? (
          <div className="flex min-h-[560px] items-center justify-center text-sm text-[var(--do-muted)]">
            Aucune machine installée ne correspond à la recherche.
          </div>
        ) : (
          <div
            className="h-[72vh] min-h-[560px]"
            data-testid="gmao-dedicated-map"
          >
            <MapContainer
              key={normalizeMapSearch(search) || "all"}
              center={centerOf(rows)}
              zoom={rows.length === 1 ? 9 : 6}
              minZoom={4}
              maxZoom={11}
              scrollWheelZoom
              className="acta-local-map h-full w-full !rounded-none"
            >
              <ActaLocalBasemap cities={rows} />

              {rows.map((cluster) => {
                const machines = cluster.machines || [];
                const color =
                  STATUS_COLORS[cluster.operationalStatus] ||
                  STATUS_COLORS.operational;

                return (
                  <CircleMarker
                    key={cluster.key}
                    center={[
                      Number(cluster.latitude),
                      Number(cluster.longitude),
                    ]}
                    radius={Math.min(
                      26,
                      9 + Math.sqrt(safeNumber(cluster.machineCount)) * 2.4
                    )}
                    pathOptions={{
                      color: "var(--do-bg-solid)",
                      fillColor: color,
                      fillOpacity: 0.88,
                      weight: 2,
                    }}
                  >
                    <Tooltip>
                      <strong>{cluster.city}</strong>
                      <br />
                      {safeNumber(cluster.machineCount)} machine(s) installée(s)
                    </Tooltip>

                    <Popup>
                      <div className="min-w-[250px] max-w-[320px] text-xs">
                        <h2 className="font-heading text-base font-black">
                          {cluster.city}
                        </h2>
                        <p className="mt-1 text-[var(--do-muted)]">
                          {safeNumber(cluster.machineCount)} machine(s) installée(s)
                        </p>

                        <div className="mt-3 max-h-72 space-y-2 overflow-y-auto pr-1">
                          {machines.map((machine) => (
                            <Link
                              key={machine.id}
                              to={`/app/gmao/v2/parc/${encodeURIComponent(
                                machine.id
                              )}`}
                              className="block rounded-lg border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 py-2 transition hover:bg-[var(--do-bg-2)]"
                              data-testid={`gmao-map-machine-${machine.id}`}
                            >
                              <strong className="block font-heading text-sm">
                                {machine.name}
                              </strong>
                              <span className="mt-0.5 block text-[11px] text-[var(--do-muted)]">
                                {machine.companyName ||
                                  [
                                    machine.clientFirstName,
                                    machine.clientLastName,
                                  ]
                                    .filter(Boolean)
                                    .join(" ") ||
                                  machine.clientName ||
                                  "Client non renseigné"}
                              </span>
                            </Link>
                          ))}
                        </div>
                      </div>
                    </Popup>
                  </CircleMarker>
                );
              })}
            </MapContainer>
          </div>
        )}
      </section>
    </main>
  );
}
